import { useState, useEffect, useRef, useCallback, useMemo } from 'react'

export default function usePagination({
  resetDependencies = [],
  itemsLength,
  pageSize,
  hasMore,
  onLoadMore,
}) {
  const [currentPage, setCurrentPage] = useState(1)
  const [targetPage, setTargetPage] = useState(1)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    setCurrentPage(1)
    setTargetPage(1)
  }, resetDependencies)

  useEffect(() => {
    const hasEnoughForTarget = itemsLength >= targetPage * pageSize

    if (hasEnoughForTarget || !hasMore) {
      setCurrentPage((prev) => (prev === targetPage ? prev : targetPage))
      return
    }

    if (!isFetchingRef.current) {
      isFetchingRef.current = true
      Promise.resolve(onLoadMore()).finally(() => {
        isFetchingRef.current = false
      })
    }
  }, [targetPage, itemsLength, pageSize, hasMore, onLoadMore])

  const totalPages = Math.max(1, Math.ceil(itemsLength / pageSize))

  const handleNext = useCallback(() => {
    const canGoNext = currentPage < totalPages || hasMore
    if (!canGoNext) return
    setTargetPage((prev) => prev + 1)
  }, [currentPage, totalPages, hasMore])

  const handlePrev = useCallback(() => {
    if (currentPage <= 1) return
    setTargetPage(currentPage - 1)
  }, [currentPage])

  const handlePageClick = useCallback(
    (page) => {
      if (page === currentPage || page < 1) return
      setTargetPage(page)
    },
    [currentPage]
  )

  const getPageNumbers = useCallback(() => {
    let startPage = currentPage === 1 ? 1 : currentPage - 1
    let endPage = currentPage === 1 ? 2 : currentPage + 1

    if (!hasMore) {
      endPage = Math.min(endPage, totalPages)
    }

    if (endPage - startPage < 2 && endPage >= 3) {
      startPage = endPage - 2
    }

    startPage = Math.max(1, startPage)

    const pages = []
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return {
      pages,
      showLeftEllipsis: startPage > 1,
      showRightEllipsis: hasMore || endPage < totalPages,
    }
  }, [currentPage, hasMore, totalPages])

  const pageRange = useMemo(
    () => ({ start: (currentPage - 1) * pageSize, end: currentPage * pageSize }),
    [currentPage, pageSize]
  )

  return {
    currentPage,
    totalPages,
    pageRange,
    handleNext,
    handlePrev,
    handlePageClick,
    getPageNumbers,
  }
}
